import { range } from "@prismicio/editor-support/Array"
import {
  ActionList,
  ActionListItem,
  BlankSlate,
  BlankSlateDescription,
  BlankSlateIcon,
  BlankSlateTitle,
  Box,
  Button,
  Dialog,
  DialogContent,
  Icon,
  ProgressCircle,
  ScrollArea,
  SearchInput,
  Separator,
  Skeleton,
  Text
} from "@prismicio/editor-ui"
import { type Dispatch, type SetStateAction, useMemo, useState } from "react"
import { Link } from "react-router-dom"

import { trackSelectDocumentInDialog } from "../../app/tracking"
import {
  useRepositoryTagsFilters,
  useRepositoryTypesFilters
} from "../DocumentListBody/ActionBar/ActionBar"
import {
  FilterDropDown,
  FilterDropDownBoundary
} from "../DocumentListBody/ActionBar/FilterDropdown"
import { FiltersBar } from "../DocumentListBody/ActionBar/FiltersBar"
import type {
  Filter,
  FilterType
} from "../DocumentListBody/ActionBar/useActionBar"
import { getFilterType } from "../DocumentListBody/ActionBar/utils"
import {
  SearchDialogContext,
  useDocumentSearch,
  useSearchDialogContext
} from "./useDocumentSearch"

interface DocumentSearchDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export function DocumentSearchDialog(props: DocumentSearchDialogProps) {
  const { isOpen, onOpenChange } = props
  const [searchTerm, setSearchTerm] = useState("")
  const [loading, setLoading] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])

  const filters = useMemo<SearchDialogContext["filters"]>(() => {
    const trimmedSearchTerm = searchTerm.trim()
    const hasFiltersApplied =
      trimmedSearchTerm !== "" || selectedFilters.length > 0

    return {
      searchTerm: trimmedSearchTerm,
      customType: getFilterType(selectedFilters, "customType"),
      tag: getFilterType(selectedFilters, "tag"),
      hasFiltersApplied
    }
  }, [selectedFilters, searchTerm])

  return (
    <Dialog
      hasClose={false}
      onOpenChange={(open) => {
        onOpenChange(open)
        if (!open) {
          setSearchTerm("")
          setSelectedFilters([])
        }
      }}
      open={isOpen}
      size={{ height: 460, width: 650 }}
    >
      <SearchDialogContext.Provider
        value={{
          filters,
          loading,
          setLoading
        }}
      >
        <DialogContent gap={0}>
          <Box minHeight={40}>
            <SearchInput
              maxLength={100}
              value={searchTerm}
              onValueChange={(value) => {
                if (value.length > 0) {
                  setLoading(true)
                }
                setSearchTerm(value)
              }}
              placeholder="Search for document titles"
              onBlur={(e) => {
                // NOTE: this disables keyboard on filter dropdown but keeps the
                // focus on the search input - which is more important
                // Ideally after [PBD-1664] we can have both
                e.target.focus()
              }}
            />
          </Box>
          <Separator />

          <ActionBar
            selectedFilters={selectedFilters}
            setSelectedFilters={(filters) => {
              setLoading(true)
              setSelectedFilters(filters)
            }}
          />
          <Separator />

          <Box flexDirection="column" overflowY="auto" height="100%">
            <DocumentList />
          </Box>

          <SearchFeedback />
        </DialogContent>
      </SearchDialogContext.Provider>
    </Dialog>
  )
}

function DocumentList() {
  const { data: documents } = useDocumentSearch()
  const { filters, loading } = useSearchDialogContext()

  const hasNoDocuments = documents.length === 0

  const trackDocumentSelection = (
    selectedDocumentPosition: number,
    totalDocuments: number
  ) => {
    trackSelectDocumentInDialog({
      numberOfResults: totalDocuments,
      numberOfWords: filters.searchTerm.split(" ").length,
      // since array position could be 0, we want to display 1 as it's more user friendly
      searchPosition: selectedDocumentPosition + 1,
      searchQuery: filters.searchTerm
    })
  }

  // No search filters applied yet
  if (hasNoDocuments && !filters.hasFiltersApplied) {
    return (
      <NoDocumentsPlaceholder
        title="Search for documents"
        subtitle="Enter keywords or apply filters to find the documents you need."
      />
    )
  }

  if (hasNoDocuments && !loading) {
    return (
      <NoDocumentsPlaceholder
        title="No documents found"
        subtitle="Consider using different keywords or tweaking your filters to find what you need."
      />
    )
  }

  const showSearchSkeleton = hasNoDocuments && loading
  const showProgressCircle = loading && !showSearchSkeleton

  return (
    <ScrollArea style={{ paddingLeft: 12, paddingRight: 1 }}>
      <Box padding={{ top: 10, bottom: 4 }} gap={8}>
        <Text variant="smallBold" color="grey11">
          Documents
        </Text>
        {showProgressCircle && <ProgressCircle color="grey11" />}
      </Box>

      {showSearchSkeleton ? (
        <SearchSkeletonLoader />
      ) : (
        <ActionList variant="borderless" gap={0}>
          {documents.map((document, index) => (
            <Link
              onClick={() => trackDocumentSelection(index, documents.length)}
              to={`/pages/${document.id}`}
              key={document.id}
              style={{ textDecoration: "none" }}
            >
              <ActionListItem
                renderStartIcon={() => <Icon name="description" size="small" />}
              >
                <HighlightedText
                  text={document.title}
                  highlight={filters.searchTerm}
                />
              </ActionListItem>
            </Link>
          ))}
        </ActionList>
      )}
    </ScrollArea>
  )
}

interface ActionBarProps {
  selectedFilters: Filter[]
  setSelectedFilters: Dispatch<SetStateAction<Filter[]>>
}

function ActionBar(props: ActionBarProps) {
  const { selectedFilters, setSelectedFilters } = props

  const { filters } = useSearchDialogContext()

  function onRemoveFilter(filterId: string, filterType: FilterType) {
    const newValues = selectedFilters?.filter(
      (filter) => filter.value !== filterId || filter.type !== filterType
    )

    setSelectedFilters(newValues)
  }

  function onFilterSelected(newFilterId: string, filterType: FilterType) {
    const alreadySelected = selectedFilters?.some(
      (type) => type.value === newFilterId && type.type === filterType
    )

    if (alreadySelected) return onRemoveFilter(newFilterId, filterType)

    const newFilters = [
      ...selectedFilters,
      { value: newFilterId, type: filterType }
    ]

    setSelectedFilters(newFilters)
  }

  return (
    <Box flexDirection="column" gap={8} padding={{ inline: 12, block: 8 }}>
      <Box alignItems="center" gap={16}>
        <FilterDropDownBoundary
          type="customType"
          selectedFilters={filters.customType}
        >
          <FilterDropDown
            type="customType"
            optionsFetcher={useRepositoryTypesFilters}
            selectedFilters={filters.customType}
            onFilterSelected={onFilterSelected}
          />
        </FilterDropDownBoundary>
        <FilterDropDownBoundary type="tag" selectedFilters={filters.tag}>
          <FilterDropDown
            type="tag"
            optionsFetcher={useRepositoryTagsFilters}
            selectedFilters={filters.tag}
            onFilterSelected={onFilterSelected}
          />
        </FilterDropDownBoundary>
      </Box>

      {selectedFilters.length > 0 && (
        <FiltersBar
          selectedFilters={selectedFilters}
          onRemoveFilter={onRemoveFilter}
        />
      )}
    </Box>
  )
}

function SearchFeedback() {
  return (
    <Box
      justifyContent="space-between"
      backgroundColor="grey3"
      padding={{ inline: 12, block: 4 }}
      width="100%"
      alignItems="center"
    >
      <Text variant="small" color="grey11">
        How was your search experience?
      </Text>
      <Button
        invisible
        color="grey"
        textWeight="normal"
        renderEndIcon={() => (
          <Icon name="sentimentSatisfied" size="small" color="grey11" />
        )}
      >
        <Text variant="smallBold" color="grey11">
          Send feedback
        </Text>
      </Button>
    </Box>
  )
}

function SearchSkeletonLoader() {
  return (
    <Box padding={{ inline: 12, top: 12 }} flexDirection="column" gap={24}>
      {range(1, 8).map((num) => (
        <Box display="flex" alignItems="center" gap={12} key={num}>
          <Icon name="description" size="small" color="grey11" />
          <Skeleton
            key={num}
            height={16}
            sx={{ flexGrow: 1 }}
            borderRadius={12}
          />
        </Box>
      ))}
    </Box>
  )
}

interface NoDocumentsPlaceholderProps {
  title: string
  subtitle?: string
}

function NoDocumentsPlaceholder(props: NoDocumentsPlaceholderProps) {
  const { title, subtitle } = props

  return (
    <BlankSlate sx={{ marginTop: 72 }}>
      <BlankSlateIcon name="search" backgroundColor="grey4" size="large" />
      <BlankSlateTitle size="big">{title}</BlankSlateTitle>
      <BlankSlateDescription width={420}>{subtitle}</BlankSlateDescription>
    </BlankSlate>
  )
}

interface HighlightedTextProps {
  text: string
  highlight: string
}

function HighlightedText(props: HighlightedTextProps) {
  const { text, highlight } = props
  const parts = text.split(new RegExp(`(${highlight})`, "gi"))
  return (
    <Text variant="normal">
      {parts.map((part, index) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <Text variant="bold" key={index}>
            {part}
          </Text>
        ) : (
          part
        )
      )}
    </Text>
  )
}
